import React, {
   createContext,
   useContext,
   useState,
   useEffect,
   useRef,
   useCallback,
   ReactNode,
} from 'react';
import { useLocation } from 'react-router-dom';
import {
   connect,
   createLocalVideoTrack,
   createLocalAudioTrack,
   Room,
   Participant,
   LocalVideoTrack,
   LocalAudioTrack,
   RemoteParticipant,
   VideoTrack,
   AudioTrack,
   Track,
   TrackPublication,
   RemoteTrackPublication,
} from 'twilio-video';
import axios from 'axios';

export interface ScheduledCallVideoState {
   isActive: boolean;
   isPopupMode: boolean;
   roomName: string;
   callTitle: string;
   callId: string;
   roomId: string;
   role: 'host' | 'participant';
   isMinimized: boolean;

   // Twilio connection state
   room: Room | null;
   isConnecting: boolean;
   isConnected: boolean;
   participants: ScheduledCallParticipant[];
   localParticipant: ScheduledCallParticipant | null;
   dominantSpeaker: ScheduledCallParticipant | null;
   currentScreenSharer: ScheduledCallParticipant | null;
   isVideoEnabled: boolean;
   isAudioEnabled: boolean;
   isScreenSharing: boolean;
   localVideoTrack: LocalVideoTrack | null;
   localAudioTrack: LocalAudioTrack | null;
   screenShareTrack: LocalVideoTrack | null;
   error: string | null;
}

export interface ScheduledCallParticipant {
   sid: string;
   identity: string;
   isConnected: boolean;
   audioTracks: AudioTrack[];
   videoTracks: VideoTrack[];
   isDominantSpeaker?: boolean;
   networkQualityLevel?: number;
   isScreenSharing?: boolean;
   lastUpdated?: number;
}

interface ScheduledCallVideoContextType {
   videoCallState: ScheduledCallVideoState;
   startScheduledCall: (params: {
      roomName: string;
      callTitle: string;
      callId: string;
      roomId: string;
      role: 'host' | 'participant';
      identity: string;
      onCallEnd?: () => void;
   }) => Promise<void>;
   endScheduledCall: () => void;
   leaveScheduledCall: () => void;
   togglePopupMode: () => void;
   setPopupMode: (isPopup: boolean) => void;
   minimizeCall: () => void;
   maximizeCall: () => void;
   isOnScheduledCallPage: boolean;

   // Twilio controls
   toggleVideo: () => Promise<void>;
   toggleAudio: () => Promise<void>;
   toggleScreenShare: () => Promise<void>;
   attachVideoTrack: () => void;
   forceReattachThumbnails: () => void;
   forceRecreateRemoteVideoElements: () => void;

   // Refs for video elements
   localVideoRef: React.RefObject<HTMLVideoElement>;
   remoteVideoContainerRef: React.RefObject<HTMLDivElement>;
}

const ScheduledCallVideoContext = createContext<ScheduledCallVideoContextType | undefined>(
   undefined
);

export const useScheduledCallVideo = () => {
   const context = useContext(ScheduledCallVideoContext);
   if (!context) {
      throw new Error('useScheduledCallVideo must be used within a ScheduledCallVideoProvider');
   }
   return context;
};

interface ScheduledCallVideoProviderProps {
   children: ReactNode;
}

// Generate unique identity to prevent duplicate identity errors
const generateUniqueIdentity = (baseIdentity: string): string => {
   const timestamp = Date.now();
   const randomSuffix = Math.random().toString(36).substr(2, 2);
   return `(${randomSuffix})${baseIdentity}`;
};

export const ScheduledCallVideoProvider: React.FC<ScheduledCallVideoProviderProps> = ({
   children,
}) => {
   const location = useLocation();

   const [videoCallState, setVideoCallState] = useState<ScheduledCallVideoState>({
      isActive: false,
      isPopupMode: false,
      roomName: '',
      callTitle: '',
      callId: '',
      roomId: '',
      role: 'participant',
      isMinimized: false,

      // Twilio connection state
      room: null,
      isConnecting: false,
      isConnected: false,
      participants: [],
      localParticipant: null,
      dominantSpeaker: null,
      currentScreenSharer: null,
      isVideoEnabled: false,
      isAudioEnabled: true,
      isScreenSharing: false,
      localVideoTrack: null,
      localAudioTrack: null,
      screenShareTrack: null,
      error: null,
   });

   // Video element refs
   const localVideoRef = useRef<HTMLVideoElement>(null);
   const remoteVideoContainerRef = useRef<HTMLDivElement>(null);

   // Ref to store callback function for when call ends
   const onCallEndCallback = useRef<(() => void) | null>(null);

   // Check if we're on the scheduled call room page
   const isOnScheduledCallPage = location.pathname.includes('/scheduled-call-room/');

   // Manual video attachment method
   const attachVideoTrack = useCallback(() => {
      if (
         videoCallState.localVideoTrack &&
         localVideoRef.current &&
         videoCallState.isVideoEnabled
      ) {
         const videoElement = localVideoRef.current;

         // Check if video is already properly attached
         const isAlreadyAttached =
            videoElement.srcObject && videoElement.videoWidth > 0 && videoElement.videoHeight > 0;

         if (isAlreadyAttached) {
            console.log('Video already properly attached, skipping reattachment');
            return;
         }

         try {
            // Detach any existing attachments
            videoCallState.localVideoTrack.detach();

            // Attach to the video element
            videoCallState.localVideoTrack.attach(videoElement);

            // Set video element properties
            videoElement.autoplay = true;
            videoElement.playsInline = true;
            videoElement.muted = true;

            // Play the video
            videoElement.play().catch((err) => {
               console.log('Video play failed:', err);
            });

            console.log('Video track manually attached successfully');
         } catch (error) {
            console.error('Failed to manually attach video track:', error);
         }
      }
   }, [videoCallState.localVideoTrack, videoCallState.isVideoEnabled]);

   // Force thumbnail reattachment for all participants
   const forceReattachThumbnails = useCallback(() => {
      console.log('Forcing thumbnail reattachment...');

      videoCallState.participants.forEach((participant) => {
         if (participant.videoTracks.length > 0 && participant.videoTracks[0]?.isEnabled) {
            // Update participant state to trigger thumbnail refresh
            setVideoCallState((prev) => ({
               ...prev,
               participants: prev.participants.map((p) =>
                  p.sid === participant.sid ? { ...p, lastUpdated: Date.now() } : p
               ),
            }));
         }
      });
   }, [videoCallState.participants]);

   // Force recreation of remote video elements (for popup mode transitions)
   const forceRecreateRemoteVideoElements = useCallback(() => {
      console.log('Context: Forcing recreation of remote video elements for popup mode');

      if (!videoCallState.room) {
         console.error('Context: No room available for recreating remote video elements');
         return;
      }

      const room = videoCallState.room;

      // Wait for remoteVideoContainerRef to be available
      const recreateElements = (attempt = 1) => {
         if (attempt > 10) {
            console.error('Context: Failed to recreate remote video elements after 10 attempts');
            return;
         }

         if (!remoteVideoContainerRef.current) {
            console.log(`Context: Remote video container not ready, retrying attempt ${attempt}`);
            setTimeout(() => recreateElements(attempt + 1), 500);
            return;
         }

         console.log(`Context: Recreating remote video elements, attempt ${attempt}`);

         // Get all remote participants from the Twilio room
         const remoteParticipants = Array.from(room.participants.values());
         console.log(`Context: Found ${remoteParticipants.length} remote participants`);

         remoteParticipants.forEach((participant) => {
            console.log(`Context: Processing participant ${participant.identity}`);

            // Process video tracks
            participant.videoTracks.forEach((publication) => {
               if (publication.isSubscribed && publication.track) {
                  const videoTrack = publication.track;
                  console.log(
                     `Context: Found video track for ${participant.identity}, track name: ${videoTrack.name}`
                  );

                  // Check if video element already exists
                  let videoElement = document.getElementById(
                     `participant-${participant.sid}-video`
                  ) as HTMLVideoElement;

                  if (!videoElement) {
                     console.log(`Context: Creating new video element for ${participant.identity}`);

                     // Create new video element
                     videoElement = document.createElement('video');
                     videoElement.id = `participant-${participant.sid}-video`;
                     videoElement.autoplay = true;
                     videoElement.playsInline = true;
                     videoElement.muted = false;
                     videoElement.style.width = '100%';
                     videoElement.style.height = '100%';
                     videoElement.style.objectFit = 'contain';

                     // Add to remote video container
                     if (remoteVideoContainerRef.current) {
                        remoteVideoContainerRef.current.appendChild(videoElement);
                        console.log(
                           `Context: Video element added to container for ${participant.identity}`
                        );
                     }
                  } else {
                     console.log(
                        `Context: Video element already exists for ${participant.identity}`
                     );
                  }

                  // Attach the video track
                  try {
                     // Detach from any existing element first
                     videoTrack.detach();

                     // Attach to our video element
                     videoTrack.attach(videoElement);
                     console.log(
                        `Context: Video track attached to element for ${participant.identity}`
                     );

                     // Check if this is a screen share track
                     const isScreenShare =
                        videoTrack.name === 'screen' ||
                        (videoTrack as any).mediaSource === 'screen';

                     if (isScreenShare) {
                        console.log(
                           `Context: Screen share track detected for ${participant.identity}`
                        );

                        // Update current screen sharer manually (avoid using convertParticipant)
                        setVideoCallState((prev) => ({
                           ...prev,
                           currentScreenSharer: {
                              sid: participant.sid,
                              identity: participant.identity,
                              isConnected: participant.state === 'connected',
                              audioTracks: [],
                              videoTracks: [],
                              isDominantSpeaker: false,
                              isScreenSharing: true,
                              lastUpdated: Date.now(),
                           },
                        }));

                        // Log the video element details
                        setTimeout(() => {
                           console.log(`Context: Screen share element status:`, {
                              hasElement: !!videoElement,
                              hasStream: !!videoElement.srcObject,
                              videoWidth: videoElement.videoWidth,
                              videoHeight: videoElement.videoHeight,
                              readyState: videoElement.readyState,
                           });
                        }, 1000);
                     }
                  } catch (error) {
                     console.error(
                        `Context: Failed to attach video track for ${participant.identity}:`,
                        error
                     );
                  }
               }
            });

            // Process audio tracks
            participant.audioTracks.forEach((publication) => {
               if (publication.isSubscribed && publication.track) {
                  const audioTrack = publication.track;

                  // Check if audio element already exists
                  let audioElement = document.getElementById(
                     `participant-${participant.sid}-audio`
                  ) as HTMLAudioElement;

                  if (!audioElement) {
                     console.log(`Context: Creating new audio element for ${participant.identity}`);

                     // Create new audio element
                     audioElement = document.createElement('audio');
                     audioElement.id = `participant-${participant.sid}-audio`;
                     audioElement.autoplay = true;

                     // Add to remote video container
                     if (remoteVideoContainerRef.current) {
                        remoteVideoContainerRef.current.appendChild(audioElement);
                     }
                  }

                  // Attach the audio track
                  try {
                     audioTrack.detach();
                     audioTrack.attach(audioElement);
                     console.log(
                        `Context: Audio track attached to element for ${participant.identity}`
                     );
                  } catch (error) {
                     console.error(
                        `Context: Failed to attach audio track for ${participant.identity}:`,
                        error
                     );
                  }
               }
            });
         });
      };

      recreateElements();
   }, [videoCallState.room]);

   // Enhanced video attachment effect with screen sharing support
   useEffect(() => {
      if (
         !videoCallState.isActive ||
         !videoCallState.localVideoTrack ||
         !videoCallState.isVideoEnabled
      ) {
         return;
      }

      const handleVideoAttachment = () => {
         if (localVideoRef.current && videoCallState.localVideoTrack) {
            const videoElement = localVideoRef.current;

            // Check if video is already properly attached
            const isAlreadyAttached =
               videoElement.srcObject &&
               videoElement.videoWidth > 0 &&
               videoElement.videoHeight > 0;

            if (isAlreadyAttached) {
               console.log('Video already properly attached, skipping reattachment');
               return;
            }

            try {
               // Detach any existing attachments
               videoCallState.localVideoTrack.detach();

               // Attach to the video element
               videoCallState.localVideoTrack.attach(videoElement);

               // Set video element properties
               videoElement.autoplay = true;
               videoElement.playsInline = true;
               videoElement.muted = true;

               // Play the video
               videoElement.play().catch((err) => {
                  console.log('Video play failed:', err);
               });

               console.log('Video track attached successfully');
            } catch (error) {
               console.error('Failed to attach video track:', error);
            }
         }
      };

      // Handle screen sharing reattachment for receivers
      const handleScreenShareReattachment = () => {
         const currentScreenSharer = videoCallState.currentScreenSharer;

         if (
            currentScreenSharer &&
            currentScreenSharer.sid !== videoCallState.localParticipant?.sid
         ) {
            console.log(
               `Context: Reattaching screen share from ${currentScreenSharer.identity} during view mode change`
            );

            const findAndReattachScreenShare = (retryCount = 0) => {
               const screenShareElement = document.getElementById(
                  `participant-${currentScreenSharer.sid}-video`
               );

               if (screenShareElement && screenShareElement instanceof HTMLVideoElement) {
                  const stream = screenShareElement.srcObject;
                  if (stream) {
                     console.log(
                        `Context: Screen share element found for ${currentScreenSharer.identity}`
                     );

                     // Force main video to display screen share
                     const mainVideoElement = document.querySelector(
                        '[data-testid="main-video"]'
                     ) as HTMLVideoElement;
                     if (mainVideoElement) {
                        mainVideoElement.srcObject = stream;
                        mainVideoElement.autoplay = true;
                        mainVideoElement.playsInline = true;
                        mainVideoElement.muted = false;

                        mainVideoElement.play().catch((error) => {
                           console.log('Context: Screen share reattachment play failed:', error);
                        });

                        console.log(`Context: Screen share reattached to main video`);
                     }

                     // Force a state update to trigger main video re-render
                     setVideoCallState((prev) => ({
                        ...prev,
                        participants: prev.participants.map((p) =>
                           p.sid === currentScreenSharer.sid ? { ...p, lastUpdated: Date.now() } : p
                        ),
                     }));
                     return;
                  }
               }

               // Retry if not found and we haven't exceeded max retries
               if (retryCount < 5) {
                  console.log(
                     `Context: Screen share element not found, retrying... (${retryCount + 1}/5)`
                  );
                  setTimeout(() => findAndReattachScreenShare(retryCount + 1), 1000);
               } else {
                  console.warn(
                     `Context: Failed to find screen share element for ${currentScreenSharer.identity} after 5 retries`
                  );
               }
            };

            findAndReattachScreenShare();
         }
      };

      // Handle screen sharing element verification
      const handleScreenShareVerification = () => {
         const currentScreenSharer = videoCallState.currentScreenSharer;

         if (
            currentScreenSharer &&
            currentScreenSharer.sid !== videoCallState.localParticipant?.sid
         ) {
            console.log(
               `Context: Verifying screen share element for ${currentScreenSharer.identity}`
            );

            const screenShareElement = document.getElementById(
               `participant-${currentScreenSharer.sid}-video`
            ) as HTMLVideoElement;

            if (screenShareElement && screenShareElement.srcObject) {
               const mainVideoElement = document.querySelector(
                  '[data-testid="main-video"]'
               ) as HTMLVideoElement;
               if (mainVideoElement) {
                  const mainStream = mainVideoElement.srcObject;
                  const screenStream = screenShareElement.srcObject;

                  if (!mainStream || mainStream !== screenStream) {
                     console.log(`Context: Screen share verification failed - reattaching`);

                     mainVideoElement.srcObject = screenStream;
                     mainVideoElement.autoplay = true;
                     mainVideoElement.playsInline = true;
                     mainVideoElement.muted = false;

                     mainVideoElement.play().catch((error) => {
                        console.log('Context: Screen share verification play failed:', error);
                     });
                  }
               }
            }
         }
      };

      // Use different delays based on the type of transition
      let delay = 800; // default delay

      if (!videoCallState.isPopupMode && !videoCallState.isMinimized) {
         // Transitioning to fullscreen mode - need longer delay
         delay = 1500;
      } else if (!videoCallState.isMinimized) {
         // Transitioning from minimized to maximized - need moderate delay
         delay = 1000;
      }

      const timer = setTimeout(() => {
         handleVideoAttachment();
         handleScreenShareReattachment();
         handleScreenShareVerification();
      }, delay);

      return () => clearTimeout(timer);
   }, [
      videoCallState.isActive,
      videoCallState.localVideoTrack,
      videoCallState.isVideoEnabled,
      videoCallState.isPopupMode, // Trigger on mode changes
      videoCallState.isMinimized, // Trigger on minimize/maximize changes
      videoCallState.currentScreenSharer?.sid, // Trigger on screen share changes
      isOnScheduledCallPage, // Trigger on page changes
   ]);

   // Auto-switch between popup and fullscreen based on location
   useEffect(() => {
      if (videoCallState.isActive) {
         if (isOnScheduledCallPage) {
            // On scheduled call page - show fullscreen
            setVideoCallState((prev) => ({
               ...prev,
               isPopupMode: false,
               isMinimized: false,
            }));
         } else {
            // On other pages - show popup
            setVideoCallState((prev) => ({
               ...prev,
               isPopupMode: true,
               isMinimized: false,
            }));

            // Force recreation of remote video elements when transitioning to popup mode
            setTimeout(() => {
               forceRecreateRemoteVideoElements();
            }, 1000);
         }
      }
   }, [isOnScheduledCallPage, videoCallState.isActive, forceRecreateRemoteVideoElements]);

   // Additional effect to handle video reattachment when switching to fullscreen
   useEffect(() => {
      if (
         videoCallState.isActive &&
         !videoCallState.isPopupMode &&
         !videoCallState.isMinimized &&
         videoCallState.isVideoEnabled &&
         videoCallState.localVideoTrack &&
         isOnScheduledCallPage
      ) {
         // This handles the transition from popup to fullscreen
         const timer = setTimeout(() => {
            if (localVideoRef.current && videoCallState.localVideoTrack) {
               const videoElement = localVideoRef.current;

               try {
                  // Force detach and reattach
                  videoCallState.localVideoTrack.detach();
                  videoCallState.localVideoTrack.attach(videoElement);

                  // Set video element properties
                  videoElement.autoplay = true;
                  videoElement.playsInline = true;
                  videoElement.muted = true;

                  // Force play
                  videoElement.play().catch((err) => {
                     console.log('Video play failed after fullscreen transition:', err);
                  });

                  console.log('Video reattached after fullscreen transition');
               } catch (error) {
                  console.error('Failed to reattach video after fullscreen transition:', error);
               }
            }
         }, 2000); // Longer delay for fullscreen transition

         return () => clearTimeout(timer);
      }
   }, [
      videoCallState.isActive,
      videoCallState.isPopupMode,
      videoCallState.isMinimized,
      videoCallState.isVideoEnabled,
      videoCallState.localVideoTrack,
      isOnScheduledCallPage,
   ]);

   // Effect to detach video track when video is disabled
   useEffect(() => {
      if (
         videoCallState.localVideoTrack &&
         !videoCallState.isVideoEnabled &&
         localVideoRef.current
      ) {
         try {
            videoCallState.localVideoTrack.detach();
            if (localVideoRef.current) {
               localVideoRef.current.srcObject = null;
            }
         } catch (error) {}
      }
   }, [videoCallState.localVideoTrack, videoCallState.isVideoEnabled]);

   // Helper function to convert Twilio participants to our format
   const convertParticipant = useCallback(
      (participant: Participant | RemoteParticipant): ScheduledCallParticipant => {
         const videoTracks: VideoTrack[] = [];
         const audioTracks: AudioTrack[] = [];

         const isRemoteParticipant = 'tracks' in participant && participant.tracks.size > 0;

         if (isRemoteParticipant) {
            const remoteParticipant = participant as RemoteParticipant;
            remoteParticipant.videoTracks.forEach((publication) => {
               if (publication.track && publication.isSubscribed) {
                  videoTracks.push(publication.track as VideoTrack);
               }
            });

            remoteParticipant.audioTracks.forEach((publication) => {
               if (publication.track && publication.isSubscribed) {
                  audioTracks.push(publication.track as AudioTrack);
               }
            });
         } else {
            participant.videoTracks.forEach((publication) => {
               if (publication.track) {
                  videoTracks.push(publication.track as VideoTrack);
               }
            });

            participant.audioTracks.forEach((publication) => {
               if (publication.track) {
                  audioTracks.push(publication.track as AudioTrack);
               }
            });
         }

         return {
            sid: participant.sid,
            identity: participant.identity,
            isConnected: participant.state === 'connected',
            audioTracks,
            videoTracks,
            isDominantSpeaker: false,
            networkQualityLevel: participant.networkQualityLevel ?? undefined,
            isScreenSharing: false,
            lastUpdated: Date.now(), // Add timestamp for tracking updates
         };
      },
      []
   );

   // Helper function to attach remote tracks to video elements
   const attachTrack = useCallback(
      (track: Track, participant: RemoteParticipant) => {
         if (track.kind === 'video') {
            // Check if this is a screen share track
            const trackName = track.name || '';
            const isScreenShare =
               trackName.includes('screen') ||
               trackName === 'screen-share' ||
               (track as any).mediaSource === 'screen';

            if (isScreenShare) {
               setVideoCallState((prev) => ({
                  ...prev,
                  currentScreenSharer: convertParticipant(participant),
               }));
            }

            // Create or get video element for this participant
            let videoElement = document.getElementById(
               `participant-${participant.sid}-video`
            ) as HTMLVideoElement;

            if (!videoElement) {
               videoElement = document.createElement('video');
               videoElement.id = `participant-${participant.sid}-video`;
               videoElement.autoplay = true;
               videoElement.playsInline = true;
               videoElement.muted = false; // Don't mute remote video
               videoElement.style.width = '100%';
               videoElement.style.height = '100%';
               videoElement.style.objectFit = isScreenShare ? 'contain' : 'cover';

               // Add debugging event listeners
               videoElement.addEventListener('loadedmetadata', () => {});

               videoElement.addEventListener('canplay', () => {});

               videoElement.addEventListener('error', (error) => {
                  console.error(`❌ Video element error for ${participant.identity}:`, error);
               });

               // Add to remote video container with retry mechanism
               const tryAppendToContainer = (retryCount = 0) => {
                  if (remoteVideoContainerRef.current) {
                     remoteVideoContainerRef.current.appendChild(videoElement);
                  } else if (retryCount < 5) {
                     // Retry up to 5 times with increasing delays
                     setTimeout(() => tryAppendToContainer(retryCount + 1), 300 * (retryCount + 1));
                  } else {
                     console.warn(
                        `⚠️ Failed to add video element to container for ${participant.identity} after 5 retries`
                     );
                  }
               };

               tryAppendToContainer();
            }

            // Attach track to video element
            try {
               (track as VideoTrack).attach(videoElement);

               // Additional debugging for screen shares
               if (isScreenShare) {
                  setTimeout(() => {
                     if (videoElement.srcObject) {
                        const stream = videoElement.srcObject as MediaStream;

                        stream.getVideoTracks().forEach((track, index) => {});
                     }
                  }, 1000);
               }
            } catch (error) {}

            // Update participant state to trigger UI refresh
            setVideoCallState((prev) => ({
               ...prev,
               participants: prev.participants.map((p) =>
                  p.sid === participant.sid
                     ? { ...p, lastUpdated: Date.now(), isScreenSharing: isScreenShare }
                     : p
               ),
            }));
         } else if (track.kind === 'audio') {
            // Create or get audio element for this participant
            let audioElement = document.getElementById(
               `participant-${participant.sid}-audio`
            ) as HTMLAudioElement;

            if (!audioElement) {
               audioElement = document.createElement('audio');
               audioElement.id = `participant-${participant.sid}-audio`;
               audioElement.autoplay = true;

               // Add to remote video container with retry mechanism
               const tryAppendToContainer = (retryCount = 0) => {
                  if (remoteVideoContainerRef.current) {
                     remoteVideoContainerRef.current.appendChild(audioElement);
                  } else if (retryCount < 5) {
                     // Retry up to 5 times with increasing delays
                     setTimeout(() => tryAppendToContainer(retryCount + 1), 300 * (retryCount + 1));
                  } else {
                     console.warn(
                        `⚠️ Failed to add audio element to container for ${participant.identity} after 5 retries`
                     );
                  }
               };

               tryAppendToContainer();
            }

            // Attach track to audio element
            (track as AudioTrack).attach(audioElement);
         }
      },
      [convertParticipant]
   );

   // Helper function to detach remote tracks from video elements
   const detachTrack = useCallback((track: Track, participant: RemoteParticipant) => {
      if (track.kind === 'video') {
         // Check if this was a screen share track
         const trackName = track.name || '';
         const isScreenShare =
            trackName.includes('screen') ||
            trackName === 'screen-share' ||
            (track as any).mediaSource === 'screen';

         if (isScreenShare) {
            setVideoCallState((prev) => ({
               ...prev,
               currentScreenSharer: null,
            }));
         }

         const videoElement = document.getElementById(
            `participant-${participant.sid}-video`
         ) as HTMLVideoElement;
         if (videoElement) {
            (track as VideoTrack).detach(videoElement);
         }
      } else if (track.kind === 'audio') {
         const audioElement = document.getElementById(
            `participant-${participant.sid}-audio`
         ) as HTMLAudioElement;
         if (audioElement) {
            (track as AudioTrack).detach(audioElement);
         }
      }
   }, []);

   // Setup room event listeners
   const setupRoomEventListeners = useCallback(
      (room: Room) => {
         // Handle participant connected
         room.on('participantConnected', (participant: RemoteParticipant) => {
            const participantData = convertParticipant(participant);
            setVideoCallState((prev) => ({
               ...prev,
               participants: [...prev.participants, participantData],
            }));

            // Handle participant's existing tracks
            participant.tracks.forEach((publication) => {
               if (publication.isSubscribed && publication.track) {
                  attachTrack(publication.track, participant);

                  // Check if this is a screen share track
                  const isScreenShare =
                     publication.track.name === 'screen' ||
                     (publication.track as any).mediaSource === 'screen';
                  if (publication.track.kind === 'video' && isScreenShare) {
                     setVideoCallState((prev) => ({
                        ...prev,
                        currentScreenSharer: convertParticipant(participant),
                     }));
                  }
               }
            });

            // Handle track subscribed
            participant.on(
               'trackSubscribed',
               (track: Track, publication: RemoteTrackPublication) => {
                  attachTrack(track, participant);

                  // Check if this is a screen share track
                  const isScreenShare =
                     track.name === 'screen' || (track as any).mediaSource === 'screen';
                  if (track.kind === 'video' && isScreenShare) {
                     setVideoCallState((prev) => ({
                        ...prev,
                        currentScreenSharer: convertParticipant(participant),
                     }));
                  }

                  // Update participant data
                  const updatedParticipant = convertParticipant(participant);
                  setVideoCallState((prev) => ({
                     ...prev,
                     participants: prev.participants.map((p) =>
                        p.sid === participant.sid ? updatedParticipant : p
                     ),
                  }));
               }
            );

            // Handle track unsubscribed
            participant.on(
               'trackUnsubscribed',
               (track: Track, publication: RemoteTrackPublication) => {
                  detachTrack(track, participant);

                  // Check if this was a screen share track
                  const isScreenShare =
                     track.name === 'screen' || (track as any).mediaSource === 'screen';
                  if (track.kind === 'video' && isScreenShare) {
                     setVideoCallState((prev) => ({
                        ...prev,
                        currentScreenSharer: null,
                     }));
                  }

                  // Update participant data
                  const updatedParticipant = convertParticipant(participant);
                  setVideoCallState((prev) => ({
                     ...prev,
                     participants: prev.participants.map((p) =>
                        p.sid === participant.sid ? updatedParticipant : p
                     ),
                  }));
               }
            );

            // Handle track enabled/disabled
            participant.on('trackEnabled', (track: Track, publication: RemoteTrackPublication) => {
               // Check if this is a screen share track
               const isScreenShare =
                  track.name === 'screen' || (track as any).mediaSource === 'screen';
               if (track.kind === 'video' && isScreenShare) {
                  setVideoCallState((prev) => ({
                     ...prev,
                     currentScreenSharer: convertParticipant(participant),
                  }));
               }

               const updatedParticipant = convertParticipant(participant);
               setVideoCallState((prev) => ({
                  ...prev,
                  participants: prev.participants.map((p) =>
                     p.sid === participant.sid ? updatedParticipant : p
                  ),
               }));
            });

            participant.on('trackDisabled', (track: Track, publication: RemoteTrackPublication) => {
               // Check if this was a screen share track
               const isScreenShare =
                  track.name === 'screen' || (track as any).mediaSource === 'screen';
               if (track.kind === 'video' && isScreenShare) {
                  setVideoCallState((prev) => ({
                     ...prev,
                     currentScreenSharer: null,
                  }));
               }

               const updatedParticipant = convertParticipant(participant);
               setVideoCallState((prev) => ({
                  ...prev,
                  participants: prev.participants.map((p) =>
                     p.sid === participant.sid ? updatedParticipant : p
                  ),
               }));
            });

            // Handle track published
            participant.on('trackPublished', (publication: RemoteTrackPublication) => {
               // Note: We can't check track.name here because track isn't available yet
               // Screen sharing detection will happen in trackSubscribed event
            });

            // Handle track unpublished
            participant.on('trackUnpublished', (publication: RemoteTrackPublication) => {
               // If this was a video track, it might have been screen sharing
               if (publication.kind === 'video') {
                  setVideoCallState((prev) => {
                     // Only clear screen sharer if this participant was the one sharing
                     if (prev.currentScreenSharer?.sid === participant.sid) {
                        return {
                           ...prev,
                           currentScreenSharer: null,
                        };
                     }
                     return prev;
                  });
               }
            });
         });

         // Handle participant disconnected
         room.on('participantDisconnected', (participant: RemoteParticipant) => {
            // If the disconnected participant was screen sharing, clear the screen sharer
            setVideoCallState((prev) => {
               const wasScreenSharing = prev.currentScreenSharer?.sid === participant.sid;
               return {
                  ...prev,
                  participants: prev.participants.filter((p) => p.sid !== participant.sid),
                  currentScreenSharer: wasScreenSharing ? null : prev.currentScreenSharer,
               };
            });

            // Clean up video elements for this participant
            const participantVideoElement = document.getElementById(
               `participant-${participant.sid}-video`
            );
            if (participantVideoElement) {
               participantVideoElement.remove();
            }

            const participantAudioElement = document.getElementById(
               `participant-${participant.sid}-audio`
            );
            if (participantAudioElement) {
               participantAudioElement.remove();
            }
         });

         // Handle dominant speaker changed
         room.on('dominantSpeakerChanged', (participant: RemoteParticipant | null) => {
            if (participant) {
               const dominantParticipant = convertParticipant(participant);
               setVideoCallState((prev) => ({
                  ...prev,
                  dominantSpeaker: dominantParticipant,
                  participants: prev.participants.map((p) => ({
                     ...p,
                     isDominantSpeaker: p.sid === participant.sid,
                  })),
               }));
            } else {
               setVideoCallState((prev) => ({
                  ...prev,
                  dominantSpeaker: null,
                  participants: prev.participants.map((p) => ({ ...p, isDominantSpeaker: false })),
               }));
            }
         });

         // Handle room disconnected
         room.on('disconnected', (room: Room, error?: Error) => {
            // Clean up all participant video elements
            const allVideoElements = document.querySelectorAll('[id^="participant-"]');
            allVideoElements.forEach((element) => element.remove());

            setVideoCallState((prev) => ({
               ...prev,
               room: null,
               isConnected: false,
               participants: [],
               localParticipant: null,
               dominantSpeaker: null,
               currentScreenSharer: null,
               error: error ? `Disconnected from room: ${error.message}` : null,
            }));
         });

         // Handle local participant's track published/unpublished events
         room.localParticipant.on('trackPublished', (publication: any) => {
            // Check if this is a screen share track
            if (publication.kind === 'video' && publication.track) {
               const trackName = publication.track.name || '';
               const isScreenShare =
                  trackName.includes('screen') ||
                  trackName === 'screen-share' ||
                  (publication.track as any).mediaSource === 'screen';
               if (isScreenShare) {
                  setVideoCallState((prev) => ({
                     ...prev,
                     currentScreenSharer: prev.localParticipant,
                     isScreenSharing: true,
                  }));
               }
            }
         });

         room.localParticipant.on('trackUnpublished', (publication: any) => {
            // Check if this was a screen share track
            if (publication.kind === 'video' && publication.track) {
               const trackName = publication.track.name || '';
               const isScreenShare =
                  trackName.includes('screen') ||
                  trackName === 'screen-share' ||
                  (publication.track as any).mediaSource === 'screen';
               if (isScreenShare) {
                  setVideoCallState((prev) => ({
                     ...prev,
                     currentScreenSharer: null,
                     isScreenSharing: false,
                  }));
               }
            }
         });
      },
      [convertParticipant, attachTrack, detachTrack]
   );

   // Generate access token from backend
   const generateAccessToken = async (
      identity: string,
      roomName: string,
      role: string
   ): Promise<string> => {
      try {
         const response = await axios.post(
            `${process.env.REACT_APP_API_URL}/api/twilio/generate-token`,
            {
               roomName,
               identity,
               role: role || 'participant',
            },
            {
               headers: {
                  'Content-Type': 'application/json',
               },
            }
         );

         if (response.data && response.data.token) {
            return response.data.token;
         } else {
            throw new Error('No token received from server');
         }
      } catch (err: any) {
         throw new Error(`Failed to generate access token: ${err.message}`);
      }
   };

   // Effect to handle call end callback
   useEffect(() => {
      if (!videoCallState.isActive && onCallEndCallback.current) {
         const callback = onCallEndCallback.current;
         onCallEndCallback.current = null;
         callback();
      }
   }, [videoCallState.isActive]);

   // Start scheduled call and connect to Twilio
   const startScheduledCall = async (params: {
      roomName: string;
      callTitle: string;
      callId: string;
      roomId: string;
      role: 'host' | 'participant';
      identity: string;
      onCallEnd?: () => void;
   }) => {
      // Store the callback function
      if (params.onCallEnd) {
         onCallEndCallback.current = params.onCallEnd;
      }
      // If already connected to the same room, don't reconnect
      if (videoCallState.isConnected && videoCallState.roomName === params.roomName) {
         setVideoCallState((prev) => ({
            ...prev,
            isActive: true,
            isPopupMode: !isOnScheduledCallPage,
            callTitle: params.callTitle,
            callId: params.callId,
            roomId: params.roomId,
            role: params.role,
            isMinimized: false,
         }));
         return;
      }

      try {
         setVideoCallState((prev) => ({
            ...prev,
            isConnecting: true,
            error: null,
         }));

         const uniqueIdentity = generateUniqueIdentity(params.identity);
         const accessToken = await generateAccessToken(
            uniqueIdentity,
            params.roomName,
            params.role
         );

         let videoTrack: LocalVideoTrack | null = null;
         let audioTrack: LocalAudioTrack | null = null;

         try {
            videoTrack = await createLocalVideoTrack({
               width: { ideal: 640 },
               height: { ideal: 480 },
               frameRate: { ideal: 24 },
               facingMode: 'user',
            });
         } catch (videoError: any) {
            try {
               videoTrack = await createLocalVideoTrack();
            } catch (fallbackError) {
               // Continue without video
            }
         }

         try {
            audioTrack = await createLocalAudioTrack();
         } catch (audioError) {
            setVideoCallState((prev) => ({
               ...prev,
               error: 'Microphone access denied or unavailable. Audio will be disabled.',
            }));
         }

         if (videoTrack) {
            // videoTrack will be set in the state below
         } else {
            setVideoCallState((prev) => ({
               ...prev,
               error: 'Camera access denied or unavailable. Video will be disabled.',
            }));
         }

         if (audioTrack) {
            // audioTrack will be set in the state below
         } else {
            setVideoCallState((prev) => ({
               ...prev,
               error: 'Microphone access denied or unavailable. Audio will be disabled.',
            }));
         }

         const tracks = [videoTrack, audioTrack].filter(Boolean) as (
            | LocalVideoTrack
            | LocalAudioTrack
         )[];

         const connectedRoom = await connect(accessToken, {
            name: params.roomName,
            tracks,
            dominantSpeaker: true,
            networkQuality: {
               local: 1,
               remote: 1,
            },
         });

         if (videoTrack) {
            videoTrack.disable(); // Start with video disabled
         }

         if (audioTrack) {
            audioTrack.enable();
         }

         const localParticipantData = convertParticipant(connectedRoom.localParticipant);
         const remoteParticipants = Array.from(connectedRoom.participants.values()).map(
            convertParticipant
         );

         setVideoCallState((prev) => ({
            ...prev,
            isActive: true,
            isPopupMode: !isOnScheduledCallPage,
            isConnecting: false,
            isConnected: true,
            roomName: params.roomName,
            callTitle: params.callTitle,
            callId: params.callId,
            roomId: params.roomId,
            role: params.role,
            isMinimized: false,
            room: connectedRoom,
            participants: remoteParticipants,
            localParticipant: localParticipantData,
            isVideoEnabled: false, // Start with video disabled
            isAudioEnabled: Boolean(audioTrack),
            localVideoTrack: videoTrack,
            localAudioTrack: audioTrack,
            error: null,
         }));

         // Setup room event listeners
         setupRoomEventListeners(connectedRoom);

         // Handle existing participants and their tracks
         connectedRoom.participants.forEach((participant: RemoteParticipant) => {
            // Handle existing tracks
            participant.tracks.forEach((publication) => {
               if (publication.isSubscribed && publication.track) {
                  attachTrack(publication.track, participant);
               }
            });

            // Set up track event listeners for existing participants
            participant.on(
               'trackSubscribed',
               (track: Track, publication: RemoteTrackPublication) => {
                  attachTrack(track, participant);

                  // Update participant data
                  const updatedParticipant = convertParticipant(participant);
                  setVideoCallState((prev) => ({
                     ...prev,
                     participants: prev.participants.map((p) =>
                        p.sid === participant.sid ? updatedParticipant : p
                     ),
                  }));
               }
            );

            participant.on(
               'trackUnsubscribed',
               (track: Track, publication: RemoteTrackPublication) => {
                  detachTrack(track, participant);

                  // Update participant data
                  const updatedParticipant = convertParticipant(participant);
                  setVideoCallState((prev) => ({
                     ...prev,
                     participants: prev.participants.map((p) =>
                        p.sid === participant.sid ? updatedParticipant : p
                     ),
                  }));
               }
            );
         });

         // Camera will remain disabled by default - user needs to manually enable it
      } catch (err: any) {
         setVideoCallState((prev) => ({
            ...prev,
            isConnecting: false,
            error: `Failed to connect to room: ${err.message}`,
         }));
      }
   };

   const leaveScheduledCall = () => {
      // Just disconnect from the room without ending the call state
      if (videoCallState.room) {
         videoCallState.room.disconnect();
      }

      // Clean up local tracks
      if (videoCallState.localVideoTrack) {
         videoCallState.localVideoTrack.stop();
      }
      if (videoCallState.localAudioTrack) {
         videoCallState.localAudioTrack.stop();
      }
      if (videoCallState.screenShareTrack) {
         videoCallState.screenShareTrack.stop();
      }

      // Set isActive to false to stop the video call, but keep call details for rejoining
      setVideoCallState((prev) => ({
         ...prev,
         isActive: false, // This was missing - needed to stop the video call
         isConnecting: false,
         isConnected: false,
         room: null,
         participants: [],
         localParticipant: null,
         dominantSpeaker: null,
         currentScreenSharer: null,
         localVideoTrack: null,
         localAudioTrack: null,
         screenShareTrack: null,
         isVideoEnabled: false,
         isAudioEnabled: false,
         isScreenSharing: false,
         error: null,
      }));
   };

   const endScheduledCall = () => {
      if (videoCallState.room) {
         videoCallState.room.disconnect();
      }

      // Clean up tracks
      if (videoCallState.localVideoTrack) {
         videoCallState.localVideoTrack.stop();
      }
      if (videoCallState.localAudioTrack) {
         videoCallState.localAudioTrack.stop();
      }
      if (videoCallState.screenShareTrack) {
         videoCallState.screenShareTrack.stop();
      }

      setVideoCallState((prev) => ({
         ...prev,
         isActive: false,
         isPopupMode: false,
         roomName: '',
         callTitle: '',
         callId: '',
         roomId: '',
         role: 'participant',
         isMinimized: false,
         room: null,
         isConnecting: false,
         isConnected: false,
         participants: [],
         localParticipant: null,
         dominantSpeaker: null,
         currentScreenSharer: null,
         isVideoEnabled: false,
         isAudioEnabled: true,
         isScreenSharing: false,
         localVideoTrack: null,
         localAudioTrack: null,
         screenShareTrack: null,
         error: null,
      }));
   };

   const togglePopupMode = () => {
      setVideoCallState((prev) => ({
         ...prev,
         isPopupMode: !prev.isPopupMode,
      }));
   };

   const setPopupMode = (isPopup: boolean) => {
      setVideoCallState((prev) => ({
         ...prev,
         isPopupMode: isPopup,
      }));
   };

   const minimizeCall = () => {
      setVideoCallState((prev) => ({
         ...prev,
         isMinimized: true,
      }));
   };

   const maximizeCall = () => {
      setVideoCallState((prev) => ({
         ...prev,
         isMinimized: false,
      }));
   };

   // Toggle video
   const toggleVideo = async () => {
      if (!videoCallState.room || !videoCallState.localVideoTrack) return;

      try {
         if (videoCallState.isVideoEnabled) {
            // Disable video
            videoCallState.localVideoTrack.disable();
            await videoCallState.room.localParticipant.unpublishTrack(
               videoCallState.localVideoTrack
            );

            setVideoCallState((prev) => ({
               ...prev,
               isVideoEnabled: false,
            }));
         } else {
            // Enable video
            videoCallState.localVideoTrack.enable();
            await videoCallState.room.localParticipant.publishTrack(videoCallState.localVideoTrack);

            setVideoCallState((prev) => ({
               ...prev,
               isVideoEnabled: true,
            }));
         }
      } catch (err: any) {
         setVideoCallState((prev) => ({
            ...prev,
            error: `Failed to toggle video: ${err.message}`,
         }));
      }
   };

   // Toggle audio
   const toggleAudio = async () => {
      if (!videoCallState.localAudioTrack) return;

      try {
         if (videoCallState.isAudioEnabled) {
            videoCallState.localAudioTrack.disable();
         } else {
            videoCallState.localAudioTrack.enable();
         }

         setVideoCallState((prev) => ({
            ...prev,
            isAudioEnabled: !prev.isAudioEnabled,
         }));
      } catch (err: any) {
         setVideoCallState((prev) => ({
            ...prev,
            error: `Failed to toggle audio: ${err.message}`,
         }));
      }
   };

   // Toggle screen share
   const toggleScreenShare = async () => {
      if (!videoCallState.room) return;

      try {
         if (videoCallState.isScreenSharing) {
            // Stop screen sharing
            if (videoCallState.screenShareTrack) {
               await videoCallState.room.localParticipant.unpublishTrack(
                  videoCallState.screenShareTrack
               );
               videoCallState.screenShareTrack.stop();

               setVideoCallState((prev) => ({
                  ...prev,
                  isScreenSharing: false,
                  screenShareTrack: null,
                  currentScreenSharer: null,
                  error: null, // Clear any previous errors
               }));
            }
         } else {
            // Check if someone else is already sharing
            if (
               videoCallState.currentScreenSharer &&
               videoCallState.currentScreenSharer.sid !== videoCallState.localParticipant?.sid
            ) {
               // Create a custom event to show toast notification
               const errorEvent = new CustomEvent('screenShareError', {
                  detail: {
                     message: `${videoCallState.currentScreenSharer?.identity || 'Someone'} is already sharing their screen. Only one person can share at a time.`,
                     severity: 'warning',
                  },
               });
               window.dispatchEvent(errorEvent);
               return;
            }

            // Start screen sharing
            let screenTrack: LocalVideoTrack | null = null;

            try {
               // Check browser support
               if (!navigator.mediaDevices || !navigator.mediaDevices.getDisplayMedia) {
                  throw new Error('Screen sharing is not supported in this browser');
               }

               const displayStream = await navigator.mediaDevices.getDisplayMedia({
                  video: true, // Request video
                  audio: false, // Don't request audio
               });

               // Get the video track from the stream
               const videoTrack = displayStream.getVideoTracks()[0];

               // Create Twilio LocalVideoTrack from the native screen track with proper naming
               screenTrack = new LocalVideoTrack(videoTrack, {
                  name: 'screen-share', // Clear name to identify as screen share
                  logLevel: 'info',
               });

               await videoCallState.room.localParticipant.publishTrack(screenTrack);

               setVideoCallState((prev) => ({
                  ...prev,
                  isScreenSharing: true,
                  screenShareTrack: screenTrack,
                  currentScreenSharer: prev.localParticipant,
                  error: null, // Clear any previous errors
               }));

               // Show success notification
               const successEvent = new CustomEvent('screenShareSuccess', {
                  detail: {
                     message: 'Screen sharing started successfully',
                     severity: 'success',
                  },
               });
               window.dispatchEvent(successEvent);

               // Handle screen share ended (when user stops sharing via browser)
               screenTrack.on('stopped', () => {
                  console.log('Screen share stopped via browser');
                  setVideoCallState((prev) => ({
                     ...prev,
                     isScreenSharing: false,
                     screenShareTrack: null,
                     currentScreenSharer: null,
                     error: null, // Clear any errors when stopping
                  }));
               });

               // Handle when the native stream ends (user stops sharing from browser UI)
               videoTrack.addEventListener('ended', () => {
                  console.log('Screen share ended by user');
                  if (screenTrack) {
                     screenTrack.stop();
                  }
                  setVideoCallState((prev) => ({
                     ...prev,
                     isScreenSharing: false,
                     screenShareTrack: null,
                     currentScreenSharer: null,
                     error: null,
                  }));
               });
            } catch (screenShareError: any) {
               console.log('Screen share error:', screenShareError);

               // Clean up any created track if publishing failed
               if (screenTrack) {
                  try {
                     screenTrack.stop();
                  } catch (stopError) {
                     console.log('Error stopping screen share track:', stopError);
                  }
               }

               // Handle different types of screen sharing errors
               let errorMessage = 'Failed to start screen sharing';
               let severity: 'error' | 'warning' | 'info' = 'error';

               if (screenShareError.message) {
                  const message = screenShareError.message.toLowerCase();

                  if (message.includes('permission denied') || message.includes('not allowed')) {
                     errorMessage = 'Screen sharing permission denied. You can try again anytime.';
                     severity = 'warning';
                     console.log('Screen sharing permission denied by user');
                  } else if (message.includes('cancelled') || message.includes('canceled')) {
                     errorMessage = 'Screen sharing was cancelled. You can try again anytime.';
                     severity = 'info';
                     console.log('Screen sharing cancelled by user');
                  } else if (message.includes('not supported')) {
                     errorMessage = 'Screen sharing is not supported in your browser.';
                     severity = 'error';
                     console.log('Screen sharing not supported');
                  } else {
                     errorMessage = `Screen sharing failed: ${screenShareError.message}`;
                     severity = 'error';
                     console.log('Screen sharing failed with error:', screenShareError.message);
                  }
               }

               // Create a custom event to show toast notification instead of setting error state
               const errorEvent = new CustomEvent('screenShareError', {
                  detail: {
                     message: errorMessage,
                     severity: severity,
                  },
               });
               window.dispatchEvent(errorEvent);

               // Reset screen sharing state without setting error
               setVideoCallState((prev) => ({
                  ...prev,
                  isScreenSharing: false,
                  screenShareTrack: null,
                  currentScreenSharer: null,
                  // Don't set error here - let the toast handle it
               }));
            }
         }
      } catch (err: any) {
         console.error('Toggle screen share error:', err);

         // Create a custom event to show toast notification
         const errorEvent = new CustomEvent('screenShareError', {
            detail: {
               message: `Failed to toggle screen share: ${err.message}`,
               severity: 'error',
            },
         });
         window.dispatchEvent(errorEvent);

         // Ensure screen sharing state is reset without setting error
         setVideoCallState((prev) => ({
            ...prev,
            isScreenSharing: false,
            screenShareTrack: null,
            currentScreenSharer: null,
            // Don't set error here - let the toast handle it
         }));
      }
   };

   const value: ScheduledCallVideoContextType = {
      videoCallState,
      startScheduledCall,
      endScheduledCall,
      leaveScheduledCall,
      togglePopupMode,
      setPopupMode,
      minimizeCall,
      maximizeCall,
      isOnScheduledCallPage,
      toggleVideo,
      toggleAudio,
      toggleScreenShare,
      attachVideoTrack,
      forceReattachThumbnails,
      forceRecreateRemoteVideoElements,
      localVideoRef,
      remoteVideoContainerRef,
   };

   return (
      <ScheduledCallVideoContext.Provider value={value}>
         {children}
      </ScheduledCallVideoContext.Provider>
   );
};
